import { useEffect, useState } from "react"
import type { RefObject } from "react"

/**
 * A React hook that detects when an element is visible in the viewport using the Intersection Observer API.
 *
 * @param ref - A ref to the element to observe
 * @param rootMargin - The root margin for the intersection observer (default: "0px")
 * @returns True if the element is intersecting with the viewport, false otherwise
 *
 * @example
 * ```tsx
 * function LazyLoadComponent() {
 *   const ref = useRef<HTMLDivElement>(null);
 *   const isOnScreen = useOnScreen(ref);
 *
 *   return (
 *     <div ref={ref}>
 *       {isOnScreen ? <ExpensiveComponent /> : <div>Loading...</div>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useOnScreen(
  ref: RefObject<Element | null>,
  rootMargin: string = "0px"
): boolean {
  // State and setter for storing whether element is visible
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Update our state when observer callback fires
        setIntersecting(entry.isIntersecting)
      },
      {
        rootMargin
      }
    )
    if (node) {
      observer.observe(node)
    }
    return () => {
      if (node) {
        observer.unobserve(node)
      }
    }
  }, [ref, rootMargin])

  return isIntersecting
}

/**
 * Callback-ref variant of `useOnScreen`. Use this when the observed element is
 * mounted lazily (e.g. inside a Portal or a `{open && ...}` branch), where a
 * `useRef`-based observer attaches before the node exists and never re-attaches.
 * Returns a setter to pass as the element's `ref` prop, plus the intersection
 * state.
 *
 * @param rootMargin - The root margin for the intersection observer (default: "0px")
 * @returns A tuple of `[setNode, isIntersecting]`
 *
 * @example
 * ```tsx
 * function LazyList() {
 *   const [setLoadMoreNode, onScreen] = useOnScreenNode("100px")
 *   return (
 *     <>
 *       {open && <div ref={setLoadMoreNode} />}
 *     </>
 *   )
 * }
 * ```
 */
export function useOnScreenNode(
  rootMargin: string = "0px"
): [(node: Element | null) => void, boolean] {
  const [node, setNode] = useState<Element | null>(null)
  const [isIntersecting, setIntersecting] = useState(false)

  useEffect(() => {
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    )
    observer.observe(node)
    return () => observer.unobserve(node)
  }, [node, rootMargin])

  return [setNode, isIntersecting]
}
